import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Star, Trophy } from "lucide-react";
import { Reveal, SectionTitle } from "../components/common";
import { aboutContent, stats, partnerBadges } from "../mock";
import { publicApi, mediaUrl } from "../lib/api";
import { getIcon } from "../lib/icons";
import xeroPlatinumCapsule from "../assets/xero-platinum-capsule.webp";
import Seo from "../components/Seo";

export default function About() {
  const [rating, setRating] = useState(null);
  const [about, setAbout] = useState(null);
  const [awards, setAwards] = useState([]);
  useEffect(() => {
    publicApi.googleRating().then(setRating).catch(() => {});
    publicApi.about().then(setAbout).catch(() => {});
    // Awards live in the footer content so there is one place to edit them.
    // Only the most recent is shown here — the full list stays in the footer.
    publicApi.footer().then((f) => setAwards((f.awards || []).filter((a) => a.label).slice(0, 1))).catch(() => {});
  }, []);
  const baseStats = stats.slice(0, 3);
  const missionHeading = about?.mission_heading || "Empowering business owners, not baffling them";
  const missionText = about?.mission || aboutContent.mission;
  const missionImage = about?.mission_image ? mediaUrl(about.mission_image, 1000) : "";
  const values = about?.values?.length ? about.values : aboutContent.values;
  return (
    <div>
      <Seo title="About Us — Your Outsourced Finance Partner" description="More than just numbers — CASS is a Xero Platinum Partner firm acting as a long-term finance partner for growing businesses, offering outsourced finance support, Virtual FD, financial reports and proactive cloud accounting across Tamworth, Lichfield, Sutton Coldfield, Birmingham, Solihull and Burton upon Trent." />
      {/* Banner */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <span className="text-orange font-bold text-sm tracking-widest uppercase">About Us</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">Giving <span className="text-orange">You</span> More<br />Than Numbers</h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">{aboutContent.intro}</p>
        </div>
      </section>

      {/* Mission + image */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            {about === null ? (
              <div data-testid="about-mission-loading" className="rounded-2xl w-full h-80 md:h-[460px] bg-slate-100 animate-pulse shadow-lg" />
            ) : missionImage ? (
              <img loading="lazy" decoding="async" src={missionImage} alt="The CASS team" data-testid="about-mission-image" className="rounded-2xl object-cover w-full h-80 md:h-[460px] shadow-lg" />
            ) : (
              <div data-testid="about-mission-empty" className="rounded-2xl w-full h-80 md:h-[460px] bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-300 text-sm shadow-lg">
                No image set — add one in Admin → About Page
              </div>
            )}
          </Reveal>
          <Reveal delay={120}>
            <span className="inline-block text-orange font-bold text-sm tracking-widest uppercase mb-3">Our mission</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy leading-tight mb-5">{missionHeading}</h2>
            <p className="text-slate-500 leading-relaxed mb-6">{missionText}</p>
            <div className="flex flex-wrap gap-3 mb-6">
              {awards.map((a) => (
                <div key={a.label} data-testid="about-award" className="flex items-center gap-2 bg-orange/5 border border-orange/25 rounded-full pl-2 pr-4 py-2">
                  <span className="w-7 h-7 rounded-full bg-orange flex items-center justify-center shrink-0"><Trophy size={14} className="text-white" /></span>
                  <span className="text-xs font-semibold text-navy">{a.label}</span>
                </div>
              ))}
              {partnerBadges.map((b) => (
                <div key={b.tag} className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-full pl-2 pr-4 py-2">
                  <span className="w-7 h-7 rounded-full bg-orange flex items-center justify-center"><Award size={14} className="text-white" /></span>
                  <span className="text-xs font-semibold text-navy">{b.label}</span>
                </div>
              ))}
            </div>
            <div className="inline-flex bg-white rounded-2xl p-4 mb-8 border border-slate-100 shadow-sm">
              <img loading="lazy" decoding="async" src={xeroPlatinumCapsule} alt="Xero Platinum Partner" className="h-12 w-auto" />
            </div>
            <div>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">Work with us <ArrowRight size={18} /></Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><SectionTitle eyebrow="What we stand for" title="Our values" center /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = getIcon(v.icon);
              return (
                <Reveal key={`${v.title}-${i}`} delay={i * 80}>
                  <div className="h-full bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                    <span className="w-12 h-12 rounded-xl bg-orange/10 flex items-center justify-center mb-5">
                      <Icon className="text-orange" size={24} />
                    </span>
                    <h3 className="font-display font-bold text-lg text-navy mb-2">{v.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {baseStats.map((st) => (
              <div key={st.label} className="text-center">
                <div className="font-display font-extrabold text-4xl md:text-5xl text-orange mb-2">{st.value}</div>
                <div className="text-sm text-slate-300">{st.label}</div>
              </div>
            ))}
            {rating?.rating ? (
              <a
                href={rating.url || undefined}
                target={rating.url ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid="about-google-rating"
                className="text-center group"
              >
                <div className="font-display font-extrabold text-4xl md:text-5xl text-orange mb-2 group-hover:opacity-80 transition-opacity">{rating.rating.toFixed(1)}/5</div>
                <div className="flex items-center justify-center gap-0.5 mb-1.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={13} className={i <= Math.round(rating.rating) ? "fill-orange text-orange" : "fill-white/25 text-white/25"} />
                  ))}
                </div>
                <div className="text-sm text-slate-300 group-hover:text-white transition-colors">{rating.total} Google reviews</div>
              </a>
            ) : (
              <div className="text-center">
                <div className="font-display font-extrabold text-4xl md:text-5xl text-orange mb-2">5★</div>
                <div className="text-sm text-slate-300">Google reviews</div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
